import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { finalize } from 'rxjs';
import { AddGroupForm } from '../../../@shared/forms/customer-groups/add-group-form/add-group-form';
import { CustomerGroupsService } from '../../../services/customer-groups/customer-groups-service';
import { CustomerService } from '../../../services/customers/customer-service';
import { PricingPlansService } from '../../../services/setting/plans/pricing-plans-service';
import { CustomerGroup, GroupMember, PlanDiscount } from '../../../models/customer-group';
import { PricingPlans } from '../../../models/pricing-plans';
import { enumToLabel } from '../../../@shared/utility/enumToOptions';

interface CustomerOption { label: string; value: number; email: string; status: string; initials: string; }
interface RawDiscount {
  planId?: number; pricingPlanId?: number;
  planName?: string; pricingPlanName?: string; name?: string;
  discountPercentage?: number; planActive?: boolean; basePrice?: number;
}
interface DiscountRow extends PlanDiscount { plan?: PricingPlans; }

@Component({
  selector: 'app-customer-group-detail',
  imports: [AddGroupForm, DecimalPipe, FormsModule, ButtonModule, DialogModule, InputNumberModule, SelectModule, RouterLink],
  templateUrl: './customer-group-detail.html',
  styleUrl: './customer-group-detail.scss',
})
export class CustomerGroupDetail implements OnInit {
  private groupsService   = inject(CustomerGroupsService);
  private customerService = inject(CustomerService);
  private plansService    = inject(PricingPlansService);
  private router          = inject(Router);
  private route           = inject(ActivatedRoute);

  @Input() group?: CustomerGroup;
  @Output() back    = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();

  groupData = signal<CustomerGroup | null>(null);
  groupLoading = signal(false);

  get g(): CustomerGroup | null { return this.groupData(); }

  members      = signal<GroupMember[]>([]);
  membersTotal  = 0;
  membersPage   = 0;
  membersHasNext = false;
  membersLoading = signal(false);
  memberSearch   = '';

  addSearch  = '';
  addOptions: CustomerOption[] = [];
  addLoading = false;
  addPage    = 0;
  addHasNext = true;
  showAddMemberDialog = false;
  selectedCustomerIds = new Set<number>();
  private addDebounce: ReturnType<typeof setTimeout> | null = null;

  mutating = signal(false);
  editDialogVisible = false;
  confirmDelete = false;
  deleting = signal(false);

  /** Member or plan discount awaiting removal confirmation. */
  pendingRemoval: { kind: 'member' | 'discount'; id: number; name: string } | null = null;
  confirmRemoveVisible = false;

  // ── Plan discounts ──────────────────────────────────
  activeTab: 'members' | 'discounts' = 'members';
  discounts: DiscountRow[] = [];
  discountsDirty = false;
  discountsSaving = signal(false);

  // ── Add new discount row ─────────────────────────────
  availablePlans: PricingPlans[] = [];
  plansLoading = false;
  showAddRow = false;
  newPlan: PricingPlans | null = null;
  newPlanLoading = false;
  newPlanDetails: PricingPlans | null = null;
  newDiscountPct = 1;

  get newFinalPrice(): number | null {
    if (!this.newPlanDetails) return null;
    const d = Math.min(Math.max(this.newDiscountPct, 0), 100);
    return +(this.newPlanDetails.finalPrice * (1 - d / 100)).toFixed(2);
  }

  get availablePlansFiltered(): PricingPlans[] {
    const existing = new Set(this.discounts.map(d => d.planId));
    return this.availablePlans.filter(p => !existing.has(p.id));
  }

  discountFinalPrice(d: PlanDiscount): number | null {
    if (d.basePrice == null) return null;
    return +(d.basePrice * (1 - d.discountPercentage / 100)).toFixed(2);
  }

  ngOnInit() {
    this.loadPlans();
    if (this.group) {
      this.groupData.set(this.group);
      this.loadMembers();
      this.initDiscounts((this.group.planDiscounts ?? []) as RawDiscount[]);
    } else {
      const id = Number(this.route.snapshot.paramMap.get('id'));
      this.groupLoading.set(true);
      this.groupsService.getGroup(id)
        .pipe(finalize(() => this.groupLoading.set(false)))
        .subscribe({ next: res => {
          this.groupData.set(res.data);
          this.loadMembers();
          this.initDiscounts((res.data.planDiscounts ?? []) as RawDiscount[]);
        }});
    }
  }

  private loadPlans() {
    this.plansLoading = true;
    this.plansService.getPricingPlansForDropdown()
      .pipe(finalize(() => this.plansLoading = false))
      .subscribe({ next: res => {
        this.availablePlans = res.data ?? [];
        this.enrichDiscountsWithBasePrice();
      }});
  }

  private initDiscounts(raw: RawDiscount[]) {
    const loadedPlans = new Map(this.discounts.map(d => [d.planId, d.plan]));
    this.discounts = raw.map(d => {
      const planId = d.planId ?? d.pricingPlanId ?? 0;
      return {
        planId,
        planName:           d.planName           ?? d.pricingPlanName ?? d.name ?? '',
        discountPercentage: d.discountPercentage ?? 0,
        planActive:         d.planActive         ?? true,
        basePrice:          d.basePrice,
        plan:               loadedPlans.get(planId),
      };
    });
    this.enrichDiscountsWithBasePrice();
  }

  private enrichDiscountsWithBasePrice() {
    if (!this.discounts.length) return;
    const planMap = new Map(this.availablePlans.map(p => [p.id, p]));
    // Try to fill from dropdown data first
    this.discounts = this.discounts.map(d => ({
      ...d,
      basePrice: d.basePrice ?? planMap.get(d.planId)?.finalPrice,
    }));
    // Fetch full plan details (prices, duration, km) for rows that don't have them yet
    this.discounts.forEach(d => {
      if (!d.plan && d.planId) {
        this.plansService.getPricingPlan(d.planId).subscribe({
          next: res => {
            this.discounts = this.discounts.map(x =>
              x.planId === d.planId ? { ...x, plan: res.data, basePrice: res.data.finalPrice } : x
            );
          }
        });
      }
    });
  }

  goBack() {
    if (this.back.observed) { this.back.emit(); }
    else { this.router.navigate(['/customer-groups']); }
  }

  onBreadcrumbClick(event: MouseEvent) {
    if (this.back.observed) {
      event.preventDefault();
      this.back.emit();
    }
  }

  openEdit() {
    setTimeout(() => { this.editDialogVisible = true; });
  }

  onGroupUpdated() {
    const id = this.groupData()!.id;
    this.groupsService.getGroup(id).subscribe({ next: res => {
      this.groupData.set(res.data);
      this.refresh.emit();
    }});
  }

  deleteGroup() {
    this.deleting.set(true);
    this.groupsService.deleteGroup(this.groupData()!.id)
      .pipe(finalize(() => this.deleting.set(false)))
      .subscribe({ next: () => {
        this.confirmDelete = false;
        this.refresh.emit();
        this.goBack();
      }});
  }

  // ── Members (GET) ────────────────────────────────────
  loadMembers(reset = true) {
    if (reset) { this.membersPage = 0; this.members.set([]); }
    this.membersLoading.set(true);
    this.groupsService.getMembers(this.groupData()!.id, { page: this.membersPage, size: 20, search: this.memberSearch || undefined })
      .pipe(finalize(() => this.membersLoading.set(false)))
      .subscribe({ next: res => {
        const page = res.data;
        this.membersTotal  = page.totalElements;
        this.membersHasNext = page.hasNext;
        if (reset) this.members.set(page.content); else this.members.update(m => [...m, ...page.content]);
      }});
  }

  loadMoreMembers() { this.membersPage++; this.loadMembers(false); }

  onMemberSearch(val: string) { this.memberSearch = val; this.loadMembers(); }

  askRemoveMember(m: GroupMember) {
    this.pendingRemoval = { kind: 'member', id: m.id, name: this.fullName(m) };
    this.confirmRemoveVisible = true;
  }

  askRemoveDiscount(d: DiscountRow) {
    this.pendingRemoval = { kind: 'discount', id: d.planId, name: d.planName };
    this.confirmRemoveVisible = true;
  }

  cancelRemoval() {
    this.confirmRemoveVisible = false;
    this.pendingRemoval = null;
  }

  confirmRemoval() {
    const p = this.pendingRemoval;
    if (!p) return;
    this.cancelRemoval();
    if (p.kind === 'member') this.removeMember(p.id);
    else this.removeDiscount(p.id);
  }

  removeMember(memberId: number) {
    this.mutating.set(true);
    this.groupsService.removeMembers(this.groupData()!.id, { customerIds: [memberId] })
      .pipe(finalize(() => this.mutating.set(false)))
      .subscribe({ next: () => { this.loadMembers(); this.refresh.emit(); } });
  }

  addMember(opt: CustomerOption) {
    this.mutating.set(true);
    this.groupsService.addMembers(this.groupData()!.id, { customerIds: [opt.value] })
      .pipe(finalize(() => this.mutating.set(false)))
      .subscribe({ next: () => {
        this.addOptions = this.addOptions.filter(o => o.value !== opt.value);
        this.loadMembers();
        this.refresh.emit();
      }});
  }

  toggleSelectCustomer(id: number) {
    if (this.selectedCustomerIds.has(id)) {
      this.selectedCustomerIds.delete(id);
    } else {
      this.selectedCustomerIds.add(id);
    }
    this.selectedCustomerIds = new Set(this.selectedCustomerIds);
  }

  saveSelectedCustomers() {
    if (!this.selectedCustomerIds.size) return;
    this.mutating.set(true);
    const ids = Array.from(this.selectedCustomerIds);
    this.groupsService.addMembers(this.groupData()!.id, { customerIds: ids })
      .pipe(finalize(() => this.mutating.set(false)))
      .subscribe({ next: () => {
        this.loadMembers();
        this.refresh.emit();
        this.closeAddMemberDialog();
      }});
  }

  closeAddMemberDialog() {
    this.showAddMemberDialog = false;
    this.addSearch = '';
    this.addOptions = [];
    this.addPage = 0;
    this.addHasNext = true;
    this.selectedCustomerIds = new Set();
  }

  openCustomer(customerId: number) { this.router.navigate(['/customers', customerId]); }

  onMemberClick(event: MouseEvent, customerId: number) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.openCustomer(customerId);
  }

  // ── Plan discounts ──────────────────────────────────
  removeDiscount(planId: number) {
    this.discounts = this.discounts.filter(d => d.planId !== planId);
    this.saveDiscounts();
  }

  onDiscountChange() { this.discountsDirty = true; }

  saveDiscounts() {
    this.discountsSaving.set(true);
    const payload = this.discounts.map(d => ({ pricingPlanId: d.planId, discountPercentage: d.discountPercentage }));
    this.groupsService.updatePlanDiscounts(this.groupData()!.id, payload)
      .pipe(finalize(() => this.discountsSaving.set(false)))
      .subscribe({ next: (res) => {
        this.initDiscounts((res.data.planDiscounts ?? []) as RawDiscount[]);
        this.discountsDirty = false;
        this.refresh.emit();
      }});
  }

  openAddRow() {
    this.newPlan = null;
    this.newPlanDetails = null;
    this.newDiscountPct = 0;
    this.showAddRow = true;
  }

  onPlanSelect(plan: PricingPlans) {
    this.newPlanDetails = null;
    if (!plan) return;
    this.newPlanLoading = true;
    this.plansService.getPricingPlan(plan.id)
      .pipe(finalize(() => this.newPlanLoading = false))
      .subscribe({ next: res => { this.newPlanDetails = res.data; } });
  }

  clampDiscount(val: number): number {
    if (val == null || isNaN(val)) return 1;
    return Math.min(100, Math.max(1, Math.round(val)));
  }

  onDiscountInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let val = parseInt(input.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    if (val > 100) val = 100;
    input.value = String(val);
    this.newDiscountPct = val;
  }

  cancelAddRow() { this.showAddRow = false; this.newPlan = null; this.newPlanDetails = null; this.newDiscountPct = 1; }

  confirmAddRow() {
    if (!this.newPlan || !this.newPlanDetails) return;
    if (!this.discounts.find(d => d.planId === this.newPlan!.id)) {
      this.discounts = [...this.discounts, {
        planId: this.newPlanDetails.id,
        planName: this.newPlanDetails.name,
        discountPercentage: this.newDiscountPct,
        planActive: this.newPlanDetails.isActive,
        basePrice: this.newPlanDetails.finalPrice,
        plan: this.newPlanDetails,
      }];
    }
    this.newPlan = null;
    this.newPlanDetails = null;
    this.newDiscountPct = 1;
    this.showAddRow = false;
    this.saveDiscounts();
  }

  // ── Add-customer search ─────────────────────────────
  onAddSearch(val: string) {
    this.addSearch = val;
    if (this.addDebounce) clearTimeout(this.addDebounce);
    this.addDebounce = setTimeout(() => { this.addPage = 0; this.addHasNext = true; this.addOptions = []; this.loadAddPage(); }, 1000);
  }

  loadAddPage() {
    if (this.addLoading || !this.addHasNext) return;
    this.addLoading = true;
    this.customerService.getCustomersList({ page: this.addPage, size: 20, sortBy: 'updatedAt', sortDirection: 'DESC', search: this.addSearch || undefined })
      .pipe(finalize(() => { this.addLoading = false; }))
      .subscribe({ next: res => {
        const existingIds = new Set(this.members().map(m => m.id));
        const items: CustomerOption[] = res.data.content
          .filter((c: { id: number | string }) => !existingIds.has(Number(c.id)))
          .map((c: { firstName: string; lastName: string; id: string | number; email?: string; status?: string }) => ({
            label:    this.fullName(c),
            value:    Number(c.id),
            email:    c.email   ?? '',
            status:   c.status  ?? '',
            initials: this.initials(c),
          }));
        const existing = new Set(this.addOptions.map(o => o.value));
        this.addOptions = [...this.addOptions, ...items.filter(i => !existing.has(i.value))];
        this.addHasNext = items.length === 20;
        this.addPage++;
      }});
  }

  /** Either name part may be empty or missing (e.g. a customer who skipped the last name). */
  fullName(p: { firstName?: string | null; lastName?: string | null }): string {
    return [p.firstName, p.lastName].map(n => n?.trim()).filter(Boolean).join(' ') || '—';
  }

  initials(p: { firstName?: string | null; lastName?: string | null }): string {
    return `${p.firstName?.trim()?.[0] ?? ''}${p.lastName?.trim()?.[0] ?? ''}`.toUpperCase() || '?';
  }

  statusStyle(status: string): { background: string; color: string } {
    const map: Record<string, { background: string; color: string }> = {
      'ACTIVE':                    { background: '#dcfce7', color: '#16a34a' },
      'BANNED':                    { background: '#fee2e2', color: '#dc2626' },
      'AWAITING_OTP_VERIFICATION': { background: '#fef9c3', color: '#854d0e' },
      'AWAITING_PAYMENT':          { background: '#fef9c3', color: '#854d0e' },
      'PAYMENT_PENDING':           { background: '#fef9c3', color: '#854d0e' },
      'PAYMENT_FAILED':            { background: '#fee2e2', color: '#dc2626' },
      'AWAITING_DOCUMENT_UPLOAD':  { background: '#ffedd5', color: '#c2410c' },
      'AWAITING_DOCUMENT_REVIEW':  { background: '#ffedd5', color: '#c2410c' },
      'DOCUMENTS_REJECTED':        { background: '#fee2e2', color: '#dc2626' },
    };
    return map[status] ?? { background: '#f1f5f9', color: '#64748b' };
  }

  getStatus(status: string) {
    return enumToLabel(status);
  }
}
